import React, { useState, useCallback } from "react";
import { Box, Stack, TextField, Button, Typography, MenuItem, Paper, Checkbox, FormControlLabel } from "@mui/material";
import AddCustomer from "../config/views/pages/direttori/add.config";
import { uploadFileToS3 } from "../../utils/s3Uploader";

const DirectorForm = ({ initialData, assigned, onSubmit, onClose }) => {
  const fieldsConfig = AddCustomer.SuperAdmin.fields;

  const [formData, setFormData] = useState(() => {
    const initial = {};
    Object.entries(fieldsConfig).forEach(([label, cfg]) => {
      if (cfg.type === "checkbox") {
        initial[cfg.fieldName] = initialData?.[cfg.fieldName] ?? false;
      } else {
        initial[cfg.fieldName] = initialData?.[cfg.fieldName] ?? "";
      }
    });

    // Valorizzo assigned e user
    initial.assigned = initialData?.assigned || assigned || "";
    initial.user = initialData?.user || initial.assigned;

    return initial;
  });

  const handleChange = useCallback((e, fieldName) => {
    setFormData(prev => ({ ...prev, [fieldName]: e.target.value }));
  }, []);

  const handleFileChange = useCallback(async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const url = await uploadFileToS3(file);
      setFormData(prev => ({ ...prev, [fieldName]: url }));
    } catch (err) {
      alert("Errore upload file: " + err.message);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = initialData ? "PUT" : "POST";
    const endpoint = initialData ? `${AddCustomer.SuperAdmin.url}/${initialData.id}` : AddCustomer.SuperAdmin.url;

    try {
      const res = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error("Errore durante il salvataggio");
      onSubmit?.();
      onClose?.();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4 }}>
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {Object.entries(fieldsConfig).map(([label, cfg]) => {
            // Checkbox
            if (cfg.type === "checkbox") {
              return (
                <FormControlLabel
                  key={cfg.fieldName}
                  control={
                    <Checkbox
                      checked={!!formData[cfg.fieldName]}
                      onChange={(e) =>
                        setFormData(prev => ({ ...prev, [cfg.fieldName]: e.target.checked }))
                      }
                    />
                  }
                  label={label}
                />
              );
            }

            // File upload
            if (cfg.type === "file-upload-s3") {
              return (
                <Box key={cfg.fieldName} display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                  <Button variant="contained" color="secondary" component="label">
                    {label}
                    <input type="file" hidden onChange={(e) => handleFileChange(e, cfg.fieldName)} />
                  </Button>
                  {formData[cfg.fieldName] && (
                    <Typography variant="caption" color="green" sx={{ wordBreak: "break-all" }}>
                      File caricato: {formData[cfg.fieldName].split("/").pop()}
                    </Typography>
                  )}
                </Box>
              );
            }

            // Enum
            if (cfg.type === "enum") {
              return (
                <TextField
                  key={cfg.fieldName}
                  select
                  label={label}
                  value={formData[cfg.fieldName]}
                  onChange={(e) => handleChange(e, cfg.fieldName)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                >
                  {cfg.choices.map(choice => (
                    <MenuItem key={choice} value={choice}>{choice}</MenuItem>
                  ))}
                </TextField>
              );
            }

            // Date
            if (cfg.type === "date") {
              return (
                <TextField
                  key={cfg.fieldName}
                  label={label}
                  type="date"
                  value={formData[cfg.fieldName]}
                  onChange={(e) => handleChange(e, cfg.fieldName)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              );
            }

            // Testo / numero
            return (
              <TextField
                key={cfg.fieldName}
                label={label}
                type={cfg.type === "number" ? "number" : "text"}
                value={formData[cfg.fieldName] || ""}
                onChange={(e) => handleChange(e, cfg.fieldName)}
                inputProps={{ maxLength: cfg.length || undefined }}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            );
          })}
        </Stack>

        <input type="hidden" name="assigned" value={formData.assigned} />
        <input type="hidden" name="user" value={formData.user} />

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
          <Button onClick={onClose} variant="outlined" color="secondary">Annulla</Button>
          <Button type="submit" variant="contained" color="primary">Salva</Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default DirectorForm;
